import { FeatureGateEnvironment } from '@atlaskit/feature-gate-js-client/types';
import { FeatureGatesInitialization } from '@atlassian/feature-gates-react';
import { useAuthData } from '@dev-agents/auth-context';
import { isProduction } from '@dev-agents/env';
import { expVal, fg } from '@dev-agents/feature-gates';
import { useTenantData } from '@dev-agents/tenant-context';
import React from 'react';

import { NotFound } from '../ui/not-found';

const STATSIG_CLIENT_KEY = {
	staging: '00f6830e-2abf-458f-b2ad-10c1a238d343',
	production: 'bf14aab7-117d-45e0-98ea-1e2e5efc2c39',
};

export { fg, expVal };

export const FeatureGateClient = ({ children }: { children: React.ReactNode }) => {
	const { tenantContexts } = useTenantData();
	const { user } = useAuthData();

	const onError = (err?: Error) => {
		if (err) {
			console.error('STATSIG_CLIENT_ERROR', err);
			throw err;
		}
	};

	// If tenantContexts is empty, the user does not have access to the site.
	if (!tenantContexts?.length) {
		return <NotFound />;
	}

	if (!user?.accountId || !tenantContexts[0].cloudId) {
		throw new Error('User/Tenant Context is not set');
	}

	// Allow overriding the Statsig environment via STATSIG_ENV env var.
	// Useful when proxying to production locally (STATSIG_ENV=production in .env).
	const statsigEnvOverride = process.env.STATSIG_ENV;
	const isProd =
		statsigEnvOverride === 'production' || (statsigEnvOverride !== 'staging' && isProduction());
	const apiKey = STATSIG_CLIENT_KEY[isProd ? 'production' : 'staging'];

	if (!apiKey) {
		throw new Error('STATSIG_CLIENT_API_KEY is not set');
	}

	const atlassianAccountId = user.accountId;
	const { cloudId, orgId } = tenantContexts[0];

	return (
		<FeatureGatesInitialization
			options={{
				apiKey,
				environment: isProd ? FeatureGateEnvironment.Production : FeatureGateEnvironment.Staging,
				targetApp: 'dev-agents_web',
				fetchTimeoutMs: 1000,
				useGatewayURL: true,
			}}
			identifiers={{ atlassianAccountId, tenantId: cloudId, atlassianOrgId: orgId }}
			onReady={onError}
		>
			{children}
		</FeatureGatesInitialization>
	);
};
